import React, { useState, useRef } from 'react';
import './FileUpload.css';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  isUploading: boolean;
  accept?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  isUploading,
  accept = 'image/jpeg,image/png,image/webp,application/pdf',
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="file-upload-wrapper">
      <div
        className={`file-upload-zone ${isDragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!selectedFile ? handleBrowseClick : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="file-input-hidden"
        />

        {!selectedFile ? (
          <div className="upload-prompt">
            <div className="upload-icon">
              <i className="fa-solid fa-cloud-arrow-up" />
            </div>
            <p className="upload-text">Drag & drop your blood test here</p>
            <p className="upload-subtext">or click to browse</p>
            <p className="upload-formats">Supports JPEG, PNG, WebP, PDF (max 20MB)</p>
          </div>
        ) : (
          <div className="selected-file-info">
            <div className="file-icon">
              <i className={`fa-solid ${selectedFile.type === 'application/pdf' ? 'fa-file-pdf' : 'fa-file-image'}`} />
            </div>
            <div className="file-details">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">{formatFileSize(selectedFile.size)}</p>
            </div>
            <button
              className="file-remove"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              aria-label="Remove file"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        )}
      </div>

      {selectedFile && (
        <button
          className="upload-submit-btn"
          onClick={handleUpload}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <span className="upload-spinner" />
              Analysing your results...
            </>
          ) : (
            <>
              <i className="fa-solid fa-microscope" />
              Analyse Blood Test
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default FileUpload;
